import { inject, Injectable } from '@angular/core';

import { WindowService } from 'src/shared/providers/window.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

/**
 * Resolves + tracks the PostHog "pm-demo-landing-page" experiment: which page a
 * property-manager/host demo visitor lands on after the "Preparing Demo" entrance,
 * and the funnel events that follow (KPI: scheduling a setup call).
 *
 * Demo visitors share the demos@tidy.com account but are NOT identified in PostHog
 * during the demo session (loginToDemoAccount never calls identify), so flags
 * evaluate against each device's anonymous distinct_id — giving independent
 * per-visitor bucketing. The resolved variant is cached in localStorage
 * (posthog_variant_*), which clearLocalStoragePreservingPosthog() preserves across
 * the demo→real conversion, so every funnel event keeps the same variant.
 *
 * Mirrors the sticky-bucketing pattern in BookService.resolveBookingVariants().
 */
@Injectable({ providedIn: 'root' })
export class PmDemoLandingService {

  private posthogService = inject(PosthogService);
  private windowService = inject(WindowService);

  // Variant -> route. 'control' keeps the current Strategy/Income overview (the
  // "Overview" desktop menu item). The others are the alternative landing pages.
  // Variant names are the PostHog experiment's, so they stay as they are.
  private static readonly VARIANT_ROUTES: Record<string, string> = {
    control: 'overview',      // Overview (StrategyPage)
    schedule: 'schedule',     // Schedule (SchedulePage)
    properties: 'properties', // Properties (DashboardPage)
    chat: 'chat',             // Chat (ChatPage)
    optimizer: 'optimizer',   // Optimizer (BookingSettingsPage)
  };

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.PM_DEMO_LANDING_PAGE
      : FEATURE_FLAGS.PM_DEMO_LANDING_PAGE_SANDBOX;
  }

  /** The currently bucketed variant (from sticky cache), for tagging funnel events. */
  getVariant(): string {
    const flagKey = this.getFlagKey();
    return localStorage.getItem(`posthog_variant_${flagKey}`)
      || (FEATURE_FLAG_DEFAULTS[flagKey] as string);
  }

  /**
   * Warms the variant cache early (e.g. when the auth modal opens) so the landing
   * navigation is instant once the user picks "For Property Managers". This evaluates
   * the flag WITHOUT recording exposure — the user is only enrolled in the experiment
   * when resolveLandingRoute() fires the exposure on the actual click. Fire-and-forget.
   */
  prefetchVariant(): void {
    this.resolveLandingVariant().catch(() => { /* warm-up only — never block the modal */ });
  }

  /**
   * Resolves the variant (sticky bucketing), records experiment exposure, and
   * returns the route to land on. Use this in place of the hard-coded 'overview'
   * navigation at every PM-demo entry point. By the time this runs (on the click),
   * prefetchVariant() has usually already cached the variant, so it returns instantly.
   */
  async resolveLandingRoute(): Promise<string> {
    const variant = await this.resolveLandingVariant();
    this.captureExposure(variant);
    return PmDemoLandingService.VARIANT_ROUTES[variant]
      || PmDemoLandingService.VARIANT_ROUTES['control'];
  }

  async resolveLandingVariant(): Promise<string> {
    const flagKey = this.getFlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    // Sticky bucketing: return cached variant if available
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }

    // Demo visitors are anonymous; identification completes (as anonymous) in
    // app.component initPostHog(). Wait for it + flags so the flag evaluates for
    // the real distinct_id, not before PostHog is ready.
    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    // No-exposure read: evaluating the flag must NOT enroll the user. Exposure is
    // recorded only by captureExposure() (the $feature_flag_called event) on the click.
    const raw = await this.posthogService.getFeatureFlagNoExposure(flagKey);
    const variant = (raw as string) || (FEATURE_FLAG_DEFAULTS[flagKey] as string);

    // Cache only a real string variant. null/false (flag off or targeting miss)
    // must not be cached — the next evaluation may return a real variant.
    if (raw !== null && raw !== undefined && raw !== false) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }

  /**
   * Records experiment exposure: $feature_flag_called enrolls the visitor in the PostHog
   * experiment and is the funnel's baseline (Exposure) step. The funnel then proceeds
   * viewed_create_account -> created_account.
   */
  private captureExposure(variant: string): void {
    this.posthogService.capture('$feature_flag_called', {
      $feature_flag: this.getFlagKey(),
      $feature_flag_response: variant,
    });
  }

  /**
   * Capture a funnel event tagged with the resolved variant and the platform form
   * factor (is_desktop), so conversion can be segmented by desktop vs. mobile.
   */
  capture(event: string, properties: Record<string, any> = {}): void {
    this.posthogService.capture(event, {
      variant: this.getVariant(),
      is_desktop: this.windowService.isDesktopRes,
      ...properties,
    });
  }
}
